import { api } from '@/lib/axios'
import { mitStand } from '@/lib/speichern'
import type {
  InheritedSlot,
  PaymentPlanItem,
  Project,
  ProjectApproval,
  ProjectDetail,
  ProjectParticipant,
  ProjectPhase,
  ProjectQualification,
  ProjectRegieRate,
  ProjectSettings,
  JournalType,
  LookupContact,
  LookupUser,
  LookupSite,
} from '../types/project.types'

/**
 * AP-35 Phase C — die Aufrufe des Projektstamms.
 *
 * Die Liste und die alten Leseaufrufe stehen in `projects.api.ts`; hier liegt,
 * was mit Phase C dazukommt: Anlegen, Kopf, Lebenslauf, Organisation und die
 * Einstellungen. Geteilt ist nur die Axios-Instanz — die Antworten kommen in der
 * Form `{ data }` wie überall.
 */

// ── Kopf und Lebenslauf ─────────────────────────────────────────────────────

export async function fetchProjectDetail(id: string): Promise<ProjectDetail> {
  const res = await api.get<{ data: ProjectDetail }>(`/projects/${id}`)
  return res.data.data
}

export async function createProject(payload: Record<string, unknown>): Promise<ProjectDetail> {
  const res = await api.post<{ data: ProjectDetail }>('/projects', payload)
  return res.data.data
}

/** `stand` ist das `updatedAt`, das die Maske kennt (AP-36 8.1) — ohne ihn schreibt der Aufruf wie bisher. */
export async function updateProject(id: string, payload: Record<string, unknown>, stand?: string): Promise<ProjectDetail> {
  const res = await api.patch<{ data: ProjectDetail }>(`/projects/${id}`, payload, mitStand(stand))
  return res.data.data
}

export async function changeProjectStatus(
  id: string,
  payload: { to: string; note?: string; completedAt?: string; warrantyEndsAt?: string },
): Promise<ProjectDetail> {
  const res = await api.post<{ data: ProjectDetail }>(`/projects/${id}/status`, payload)
  return res.data.data
}

export async function fetchProjectHistory(id: string): Promise<ProjectApproval[]> {
  const res = await api.get<{ data: ProjectApproval[] }>(`/projects/${id}/history`)
  return res.data.data
}

// ── Phasen & Zuständige ─────────────────────────────────────────────────────

export interface PhaseSlotsAntwort {
  phases: ProjectPhase[]
  slots: InheritedSlot[]
  assemblyFixed: { date: string | null; time: string | null; note: string | null } | null
  deviatingPositions?: { phaseId: string; positions: number }[]
}

export async function fetchPhaseSlots(projectId: string): Promise<PhaseSlotsAntwort> {
  const res = await api.get<{ data: PhaseSlotsAntwort }>(`/projects/${projectId}/phase-slots`)
  return res.data.data
}

export async function savePhaseSlots(
  projectId: string,
  slots: Record<string, unknown>[],
  stand?: string,
): Promise<PhaseSlotsAntwort> {
  const res = await api.put<{ data: PhaseSlotsAntwort }>(`/projects/${projectId}/phase-slots`, { slots }, mitStand(stand))
  return res.data.data
}

export async function fetchParticipants(
  projectId: string,
): Promise<{ participants: ProjectParticipant[]; clientContact: LookupContact | null }> {
  const res = await api.get<{ data: { participants: ProjectParticipant[]; clientContact: LookupContact | null } }>(
    `/projects/${projectId}/participants`,
  )
  return res.data.data
}

export async function addParticipant(projectId: string, payload: Record<string, unknown>) {
  const res = await api.post(`/projects/${projectId}/participants`, payload)
  return res.data
}

export async function updateParticipant(projectId: string, id: string, payload: Record<string, unknown>, stand?: string) {
  const res = await api.patch(`/projects/${projectId}/participants/${id}`, payload, mitStand(stand))
  return res.data
}

export async function removeParticipant(projectId: string, id: string) {
  await api.delete(`/projects/${projectId}/participants/${id}`)
}

// ── Abrechnung ──────────────────────────────────────────────────────────────

export interface RegieRatesAntwort {
  own: ProjectRegieRate[]
  tenantDefaults: ProjectRegieRate[]
  qualifications: ProjectQualification[]
}

export async function fetchProjectRegieRates(projectId: string): Promise<RegieRatesAntwort> {
  const res = await api.get<{ data: RegieRatesAntwort }>(`/projects/${projectId}/regie-rates`)
  return res.data.data
}

export async function setProjectRegieRate(projectId: string, payload: Record<string, unknown>) {
  const res = await api.post(`/projects/${projectId}/regie-rates`, payload)
  return res.data
}

/** Direkt in der Tabelle ändern (AP-35 Nachtrag 18.9.2026). */
export async function updateProjectRegieRate(projectId: string, id: string, payload: Record<string, unknown>) {
  const res = await api.patch(`/projects/${projectId}/regie-rates/${id}`, payload)
  return res.data
}

export async function deleteProjectRegieRate(projectId: string, id: string) {
  await api.delete(`/projects/${projectId}/regie-rates/${id}`)
}

export async function fetchPaymentPlan(projectId: string): Promise<PaymentPlanItem[]> {
  const res = await api.get<{ data: PaymentPlanItem[] }>(`/projects/${projectId}/payment-plan`)
  return res.data.data
}

export async function savePaymentPlan(projectId: string, items: Record<string, unknown>[]) {
  const res = await api.put<{ data: PaymentPlanItem[] }>(`/projects/${projectId}/payment-plan`, { items })
  return res.data.data
}

export interface NextDueAntwort {
  /** Wirksames Schema — auch wenn das Projekt es vom Mandanten erbt. */
  scheme: string
  interval: string
  lastInvoiceAt: string | null
  lastInvoiceNumber: string | null
  nextDueAt: string | null
}

export async function fetchNextDue(projectId: string): Promise<NextDueAntwort> {
  const res = await api.get<{ data: NextDueAntwort }>(
    `/projects/${projectId}/billing/next-due`,
  )
  return res.data.data
}

export async function setBillingDone(projectId: string, note?: string) {
  const res = await api.post(`/projects/${projectId}/billing-done`, { note })
  return res.data
}

// ── Auswahlhilfen ───────────────────────────────────────────────────────────

export async function fetchUsers(): Promise<LookupUser[]> {
  const res = await api.get<{ data: LookupUser[] }>('/projects/lookups/users')
  return res.data.data
}

export async function searchContacts(q: string): Promise<LookupContact[]> {
  const res = await api.get<{ data: LookupContact[] }>('/projects/lookups/contacts', { params: { q } })
  return res.data.data
}

export async function fetchSites(): Promise<LookupSite[]> {
  const res = await api.get<{ data: LookupSite[] }>('/projects/lookups/sites')
  return res.data.data
}

// ── Einstellungen (Detailblatt 13) ──────────────────────────────────────────

export async function fetchProjectSettings(): Promise<ProjectSettings> {
  const res = await api.get<{ data: ProjectSettings }>('/projects/settings')
  return res.data.data
}

export async function saveProjectSettings(tab: string, payload: Record<string, unknown>): Promise<ProjectSettings> {
  const res = await api.patch<{ data: ProjectSettings }>(`/projects/settings/${tab}`, payload)
  return res.data.data
}

export async function fetchPhases(): Promise<ProjectPhase[]> {
  const res = await api.get<{ data: ProjectPhase[] }>('/projects/settings/phases')
  return res.data.data
}

export async function createPhase(payload: Record<string, unknown>) {
  const res = await api.post('/projects/settings/phases', payload)
  return res.data
}

export async function updatePhase(id: string, payload: Record<string, unknown>) {
  const res = await api.patch(`/projects/settings/phases/${id}`, payload)
  return res.data
}

export async function deletePhase(id: string) {
  await api.delete(`/projects/settings/phases/${id}`)
}

export async function fetchQualifications(): Promise<ProjectQualification[]> {
  const res = await api.get<{ data: ProjectQualification[] }>('/projects/settings/qualifications')
  return res.data.data
}

export async function createQualification(payload: Record<string, unknown>) {
  const res = await api.post('/projects/settings/qualifications', payload)
  return res.data
}

export async function updateQualification(id: string, payload: Record<string, unknown>) {
  const res = await api.patch(`/projects/settings/qualifications/${id}`, payload)
  return res.data
}

export async function fetchTenantRegieRates(): Promise<ProjectRegieRate[]> {
  const res = await api.get<{ data: ProjectRegieRate[] }>('/projects/settings/regie-rates')
  return res.data.data
}

export async function createTenantRegieRate(payload: Record<string, unknown>) {
  const res = await api.post('/projects/settings/regie-rates', payload)
  return res.data
}

/** AP-36: mit `stand` (X-Stand) antwortet der Server bei einem überholten Satz mit 409. */
export async function updateTenantRegieRate(id: string, payload: Record<string, unknown>, stand?: string) {
  const res = await api.patch(`/projects/settings/regie-rates/${id}`, payload, stand ? { headers: { 'X-Stand': stand } } : undefined)
  return res.data
}

export async function deleteTenantRegieRate(id: string) {
  await api.delete(`/projects/settings/regie-rates/${id}`)
}

export async function deleteQualification(id: string) {
  await api.delete(`/projects/settings/qualifications/${id}`)
}

export async function createJournalType(payload: Record<string, unknown>) {
  const res = await api.post('/projects/settings/journal-types', payload)
  return res.data
}

export async function fetchJournalTypes(): Promise<JournalType[]> {
  const res = await api.get<{ data: JournalType[] }>('/projects/settings/journal-types')
  return res.data.data
}

export async function updateJournalType(id: string, payload: Record<string, unknown>) {
  const res = await api.patch(`/projects/settings/journal-types/${id}`, payload)
  return res.data
}

/** Kunden für die Projektmaske — dieselbe Auswahl wie im Belegwesen. */
export async function searchClients(q: string): Promise<{ id: string; name: string; clientNumber: string }[]> {
  const res = await api.get<{ data: { id: string; name: string; clientNumber: string }[] }>('/clients', {
    // Phase K: `role` statt `isCustomer` — den Parameter kennt die Kundenliste
    // nicht, und `forbidNonWhitelisted` beantwortete die Suche mit 400.
    params: { search: q, limit: 20, role: 'customer' },
  })
  return res.data.data ?? []
}

export type { Project }

/** Ansprechpartner und Adressen eines Kunden — für die Karten des Projektkopfs. */
export interface PartnerAuswahl {
  contacts: { id: string; firstName: string; lastName: string | null; email: string | null; phone: string | null; position: string | null }[]
  addresses: {
    id: string
    type: string
    label: string | null
    recipient: string | null
    street: string
    addition: string | null
    zip: string
    city: string
    country: string
    isDefault: boolean
  }[]
}

export async function fetchPartner(clientId: string): Promise<PartnerAuswahl> {
  const res = await api.get<{ data: PartnerAuswahl }>(`/projects/lookups/clients/${clientId}/partner`)
  return res.data.data
}

// ── Nachtrag 2: Auswahl ohne CRM-Recht, weitere Adressen ────────────────────

/** Kunden suchen — über das Projektmodul, nicht über `/clients` (braucht kein CRM-Recht). */
export async function lookupClients(q: string, allePartner = false): Promise<{ id: string; name: string; clientNumber: string }[]> {
  const res = await api.get<{ data: { id: string; name: string; clientNumber: string }[] }>('/projects/lookups/clients', {
    params: { q, ...(allePartner ? { all: 1 } : {}) },
  })
  return res.data.data
}

export interface PartnerAdresse {
  id: string
  type: string
  label: string | null
  recipient: string | null
  street: string
  addition: string | null
  zip: string
  city: string
  country: string
  client: { id: string; name: string } | null
}

/** Adressen **aller** Geschäftspartner — Name, Straße, Ort oder PLZ. */
export async function lookupAddresses(q: string): Promise<PartnerAdresse[]> {
  const res = await api.get<{ data: PartnerAdresse[] }>('/projects/lookups/addresses', { params: { q } })
  return res.data.data
}

export async function saveExtraAddresses(
  projectId: string,
  items: { label: string; addressId?: string | null; addressJson?: Record<string, unknown> | null }[],
): Promise<ProjectDetail> {
  const res = await api.put<{ data: ProjectDetail }>(`/projects/${projectId}/extra-addresses`, { items })
  return res.data.data
}

/** Nachtrag 3 — Termine (Phasen-Slots) einer Position, mit `own`/`effective`. */
export async function fetchPositionPhaseSlots(projectId: string, positionId: string): Promise<PhaseSlotsAntwort> {
  const res = await api.get<{ data: PhaseSlotsAntwort }>(`/projects/${projectId}/positions/${positionId}/phase-slots`)
  return res.data.data
}

/** Leere Slots gehen zurück auf das Projekt (der Server löscht sie). */
export async function savePositionPhaseSlots(
  projectId: string,
  positionId: string,
  slots: Record<string, unknown>[],
  stand?: string,
): Promise<PhaseSlotsAntwort> {
  const res = await api.put<{ data: PhaseSlotsAntwort }>(
    `/projects/${projectId}/positions/${positionId}/phase-slots`,
    { slots },
    mitStand(stand),
  )
  return res.data.data
}
